/**
 * The cookie and tracking notice, and the control that withdraws it.
 *
 * LAUNCH_PLAN task 92. The plan's requirement is the one sentence worth repeating: *"a banner
 * that does not suppress collection is worse than none."* What this component does is ask; what
 * makes the answer real is `src/utils/trackingConsent.ts`, which every sender consults —
 * `monitoring.trackEvent`, `monitoring.recordMetric`, `monitoring.sendErrorToService`, and both
 * of Sentry's own pipelines (`beforeSend` AND `beforeSendTransaction`, because the SDK sends on
 * its own and gating one leaves the other running).
 *
 * WHAT IS NOT ASKED. Essential storage — the session, the CSRF nonce, the screen-time clock — is
 * not offered as a choice, because offering it would be asking whether the product may work. The
 * notice says so rather than leaving a reader to wonder what "essential" covers.
 *
 * A CHILD IS NOT ASKED AT ALL. Under COPPA the permission that counts is a parent's, and a
 * ten-year-old pressing Accept is not it. When the signed-in account is COPPA-covered the notice
 * does not appear and every non-essential category is off — see `lockForChildAccount`.
 *
 * ACCESSIBILITY. It is a `role="region"`, not a modal: the sale path runs through a VPAT, and a
 * blocking dialog a screen-reader user cannot escape is a WCAG finding (the same reason there is
 * no `alert()` anywhere in this codebase). Nothing is focus-trapped, the page is usable behind
 * it, and both buttons are reachable by keyboard in reading order. Dismissing it without choosing
 * leaves consent DENIED, so the quiet path is the private one.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  getTrackingConsent,
  isConsentLocked,
  onTrackingConsentChange,
  resetTrackingConsent,
  setTrackingConsent,
  TrackingConsent,
} from '../../utils/trackingConsent';
import './CookieConsent.css';

/**
 * The CSS custom property the rest of the page reads to keep out from under the notice.
 * `src/index.css` turns it into `padding-bottom` on <body> and `scroll-padding-bottom` on
 * <html>, and the two full-height layouts (the editor, the collaboration view) subtract it.
 */
export const BANNER_HEIGHT_VAR = '--cookie-banner-height';

/**
 * Publish the notice's real height while it is on screen, and withdraw it when it goes.
 *
 * WHY (2026-10-05, the first E2E run against staging): the notice is fixed to the bottom of
 * the viewport, and nothing reserved its space — so it sat ON TOP of whatever was there. Seven
 * of the eight failures in that run were the same thing: "Let's Build It!", "Sign in", "Delete
 * My Account" and the chat Send button were all under it, and a click landed on the notice
 * instead. The comment in `App.tsx` said "the page behind it stays usable"; it did not.
 *
 * A keyboard user had it worse than a mouse user: Tab could land on a control that was
 * entirely hidden behind the notice (WCAG 2.2 SC 2.4.11, Focus Not Obscured). The
 * `scroll-padding-bottom` this feeds is what makes the browser scroll a focused control clear.
 *
 * Measured with a ResizeObserver rather than a constant, because the height changes — "What
 * exactly is collected?" expands it, and it wraps differently on a phone.
 */
function useReservedBottomSpace(active: boolean): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = ref.current;
    if (!active || !node) {
      root.style.removeProperty(BANNER_HEIGHT_VAR);
      return undefined;
    }
    const publish = () => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${Math.ceil(node.getBoundingClientRect().height)}px`);
    };
    publish();
    // jsdom has no ResizeObserver; one measurement is the most it could offer anyway.
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      // Withdrawn on answer and on unmount, or the page keeps a gap where the notice was.
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [active]);

  return ref;
}

export const CookieConsentBanner: React.FC = () => {
  const [consent, setConsent] = useState<TrackingConsent>(() => getTrackingConsent());
  const [locked, setLocked] = useState<boolean>(() => isConsentLocked());
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    // A child signing in on a browser where an adult accepted has to stop the sending NOW, not
    // at the next page load — so this listens rather than reading once.
    const unsubscribe = onTrackingConsentChange((next) => {
      setConsent(next);
      setLocked(isConsentLocked());
    });
    return unsubscribe;
  }, []);

  const visible = !locked && !consent.decided;
  // Before the early return: hooks cannot be conditional.
  const sectionRef = useReservedBottomSpace(visible);

  if (!visible) return null;

  // A `<section>` with an accessible name IS a region in the ARIA mapping, so an explicit
  // `role="region"` is redundant — `aria-label` is what makes it one and what a screen reader
  // announces. It is NOT a dialog: see the header for why nothing here is focus-trapped.
  return (
    <section
      ref={sectionRef}
      className="cookie-consent"
      aria-label="Cookie and tracking choices"
    >
      <div className="cc-body">
        <h2 className="cc-title">Your privacy choices</h2>
        <p>
          We always store what the site needs to work — keeping you signed in, keeping your
          account secure, and counting screen time if a parent has set a limit. We never ask
          permission for those, because without them the site cannot work.
        </p>
        <p>
          Separately, we can send anonymous usage and crash information to help us find problems.
          That leaves your device. <strong>You can say no, and nothing will be sent.</strong>
        </p>

        {showDetail && (
          <ul className="cc-detail">
            <li>
              <strong>Remembered preferences</strong> — which tab you had open, a panel you
              collapsed, an unfinished message. Stored on this device only; never sent anywhere.
            </li>
            <li>
              <strong>Usage and crash reports</strong> — which pages are slow, and the technical
              details of an error. Sent to a service that helps us fix things. Includes your IP
              address, because every request does. Never your name, email or anything you type.
            </li>
          </ul>
        )}

        <button
          type="button"
          className="cc-link"
          onClick={() => setShowDetail((shown) => !shown)}
          aria-expanded={showDetail}
        >
          {showDetail ? 'Hide the details' : 'What exactly is collected?'}
        </button>
      </div>

      <div className="cc-actions">
        <button
          type="button"
          className="cc-btn cc-btn-secondary"
          onClick={() => setTrackingConsent({ functional: false, analytics: false })}
        >
          Reject everything optional
        </button>
        <button
          type="button"
          className="cc-btn cc-btn-secondary"
          onClick={() => setTrackingConsent({ functional: true, analytics: false })}
        >
          Preferences only
        </button>
        <button
          type="button"
          className="cc-btn cc-btn-primary"
          onClick={() => setTrackingConsent({ functional: true, analytics: true })}
        >
          Accept all
        </button>
      </div>
    </section>
  );
};

/**
 * The withdrawal control, for Settings.
 *
 * Withdrawing has to be as easy as consenting, and it has to be findable after the banner is
 * gone — a choice that can only be made once is not a choice. It says what is currently on, so a
 * viewer can see the answer rather than remember it.
 */
export const CookieConsentSettings: React.FC = () => {
  const [consent, setConsent] = useState<TrackingConsent>(() => getTrackingConsent());
  const [locked, setLocked] = useState<boolean>(() => isConsentLocked());

  useEffect(() => onTrackingConsentChange((next) => {
    setConsent(next);
    setLocked(isConsentLocked());
  }), []);

  if (locked) {
    return (
      <section className="cc-settings" aria-labelledby="cc-settings-heading">
        <h3 id="cc-settings-heading">Privacy choices</h3>
        <p>
          This account belongs to a child, so we do not collect anything optional and there is
          nothing to turn on here. A parent can change what this account may do from their own
          Permissions screen.
        </p>
      </section>
    );
  }

  return (
    <section className="cc-settings" aria-labelledby="cc-settings-heading">
      <h3 id="cc-settings-heading">Privacy choices</h3>
      <p className="cc-current">
        {consent.analytics
          ? 'Usage and crash reports: ON.'
          : 'Usage and crash reports: OFF — nothing is being sent.'}{' '}
        {consent.functional
          ? 'Remembered preferences: ON.'
          : 'Remembered preferences: OFF.'}
      </p>
      <div className="cc-actions">
        <button
          type="button"
          className="cc-btn cc-btn-secondary"
          onClick={() => setTrackingConsent({ functional: false, analytics: false })}
        >
          Turn everything optional off
        </button>
        <button
          type="button"
          className="cc-btn cc-btn-secondary"
          onClick={() => setTrackingConsent({ functional: true, analytics: true })}
        >
          Turn it all on
        </button>
        <button type="button" className="cc-link" onClick={resetTrackingConsent}>
          Ask me again
        </button>
      </div>
    </section>
  );
};

export default CookieConsentBanner;
